import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { API } from '@/lib/api';
import axios from 'axios';
import { Loader2, CheckCircle2, Upload, X } from 'lucide-react';

const LIFE_IMAGES = [
  { src: 'https://images.pexels.com/photos/3862154/pexels-photo-3862154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', caption: 'Working together, thinking out loud' },
  { src: 'https://images.pexels.com/photos/8278915/pexels-photo-8278915.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', caption: 'From whiteboard to shipping in the same week' },
  { src: 'https://images.pexels.com/photos/340152/pexels-photo-340152.jpeg', caption: 'Deep work · uninterrupted' },
  { src: 'https://images.pexels.com/photos/9553905/pexels-photo-9553905.jpeg', caption: 'Every pixel matters' },
  { src: 'https://images.pexels.com/photos/16323454/pexels-photo-16323454.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', caption: 'Different backgrounds, one mission' },
  { src: 'https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', caption: 'The best debuggers are two heads on one screen' },
];

const MAX_MB = 5;
const ALLOWED_EXT = ['.pdf', '.doc', '.docx', '.rtf', '.txt'];

export default function CareersPage() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', role: '', location: '', experience: '', message: '',
  });
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setResume(null); return; }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: 'File too large', description: `Please upload a file under ${MAX_MB} MB.` });
      e.target.value = '';
      return;
    }
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast({ title: 'Unsupported format', description: `Allowed: ${ALLOWED_EXT.join(', ')}` });
      e.target.value = '';
      return;
    }
    setResume(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: 'Missing fields', description: 'Name, email and message are required.' });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      if (resume) fd.append('resume', resume);
      const { data } = await axios.post(`${API}/careers/apply`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSent(true);
      toast({
        title: 'Application received',
        description: data?.candidate_ack
          ? 'A confirmation has been sent to your email.'
          : 'Our team will reach out if your profile is a match.',
      });
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: err?.response?.data?.detail || 'Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Life at Visotonics — photo collage */}
      <section className="pt-16 md:pt-20 pb-20 bg-[#0a0d12]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300 mb-3">Life at Visotonics</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
            Curious people. Real problems. A little bit of magic.
          </h2>

          <div className="mt-10 grid grid-cols-6 gap-3 md:gap-4 auto-rows-[130px] md:auto-rows-[170px]">
            {[
              { i: 0, span: 'col-span-6 md:col-span-3 row-span-2' },
              { i: 1, span: 'col-span-3 md:col-span-2 row-span-2' },
              { i: 2, span: 'col-span-3 md:col-span-1 row-span-2' },
              { i: 3, span: 'col-span-3 md:col-span-2 row-span-2' },
              { i: 4, span: 'col-span-3 md:col-span-2 row-span-2' },
              { i: 5, span: 'col-span-6 md:col-span-2 row-span-2' },
            ].map(({ i, span }) => (
              <div
                key={i}
                className={`group relative overflow-hidden rounded-2xl border border-white/10 ${span}`}
              >
                <img
                  src={LIFE_IMAGES[i].src}
                  alt={LIFE_IMAGES[i].caption}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07090c]/85 via-[#07090c]/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-xs md:text-sm text-slate-100 font-medium opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all">
                  {LIFE_IMAGES[i].caption}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="py-20 border-t border-white/5 scroll-mt-24 bg-[#0a0d12]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-cyan-300 mb-3">Introduce yourself</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold max-w-3xl leading-tight">
            Tell us about you.
          </h2>
          <p className="text-slate-400 mt-4 max-w-2xl">
            A few lines about you, the problems you love solving, and (optionally) a resume. If there is a match — now or soon — a real person from our team will reach out.
          </p>

          {sent ? (
            <div className="mt-10 rounded-2xl border border-cyan-400/20 bg-cyan-400/[0.04] p-10 text-center">
              <CheckCircle2 className="w-10 h-10 text-cyan-300 mx-auto" />
              <div className="font-display text-2xl mt-4">Thanks for reaching out.</div>
              <p className="text-slate-300 mt-3 max-w-md mx-auto">
                A confirmation has been sent to <span className="text-cyan-300">{form.email}</span>. Our team reviews every note personally — we&apos;ll be in touch if your profile is a match.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <Link to="/company/about">
                  <Button variant="outline" className="border-white/15 bg-white/5 hover:bg-white/10 text-slate-100">About Visotonics</Button>
                </Link>
                <Link to="/platform">
                  <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">Explore the platform</Button>
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-10 rounded-2xl border border-white/10 bg-white/[0.03] p-7 grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Full name *</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="Jane Doe" />
              </div>
              <div>
                <Label className="text-slate-300">Email *</Label>
                <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="jane@email.com" />
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="+00 0000 000 000" />
              </div>
              <div>
                <Label className="text-slate-300">Area of interest</Label>
                <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="Engineering, Product, Research, Sales..." />
              </div>
              <div>
                <Label className="text-slate-300">Where are you based?</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="City" />
              </div>
              <div>
                <Label className="text-slate-300">Experience</Label>
                <Select value={form.experience} onValueChange={(v) => setForm({ ...form, experience: v })}>
                  <SelectTrigger className="bg-black/30 border-white/10 mt-1"><SelectValue placeholder="Total years" /></SelectTrigger>
                  <SelectContent>
                    {['0-1 years', '2-4 years', '5-7 years', '8-12 years', '13+ years'].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300">Why Visotonics? *</Label>
                <Textarea rows={4} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-black/30 border-white/10 mt-1" placeholder="Tell us about a real problem you have shipped, and why you want to build with us." />
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300">Resume (optional)</Label>
                <div className="mt-1 flex items-center gap-3 flex-wrap">
                  <label htmlFor="resume-upload" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-white/[0.04] border border-white/10 text-sm text-slate-200 hover:border-cyan-400/40 hover:bg-white/[0.08] cursor-pointer transition-colors">
                    <Upload className="w-4 h-4 text-cyan-300" />
                    {resume ? 'Change file' : 'Upload resume'}
                  </label>
                  <input
                    id="resume-upload"
                    type="file"
                    accept=".pdf,.doc,.docx,.rtf,.txt"
                    onChange={onFile}
                    className="hidden"
                  />
                  {resume && (
                    <div className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="truncate max-w-[240px]">{resume.name}</span>
                      <span className="text-xs text-slate-500">({(resume.size / 1024).toFixed(0)} KB)</span>
                      <button type="button" onClick={() => setResume(null)} className="text-slate-400 hover:text-rose-300" aria-label="Remove file">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">PDF, DOC, DOCX, RTF or TXT · under 5 MB · optional.</p>
              </div>
              <div className="md:col-span-2">
                <Button disabled={loading} className="w-full md:w-auto bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                  {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Submit application
                </Button>
                <p className="text-[11px] text-slate-500 mt-3">
                  By submitting, you agree to Visotonics processing your details for recruitment purposes as described in our <Link to="/legal/privacy" className="text-cyan-300">Privacy Policy</Link>.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
